/**
 * useDesktopNotifications — React hook for browser Notification API.
 *
 * Uses the simple Notification API (not Web Push / service workers) since the
 * admin UI is already open with a live Socket.IO connection. Notifications
 * only fire when the browser tab is hidden (document.hidden === true) to
 * avoid duplicating the in-app toast.
 *
 * Usage:
 *   const { isSupported, permission, requestPermission, sendNotification } = useDesktopNotifications();
 */

import { useState, useCallback } from 'react';

const AEROCHAT_ICON = '/aerochat-emblem.png';

export function useDesktopNotifications() {
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  const [permission, setPermission] = useState(
    isSupported ? Notification.permission : 'denied'
  );

  /**
   * Request browser notification permission.
   * Returns the resulting permission string ('granted' | 'denied' | 'default').
   */
  const requestPermission = useCallback(async () => {
    if (!isSupported) return 'denied';
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      return 'denied';
    }
  }, [isSupported]);

  /**
   * Show a desktop notification (only when tab is hidden).
   * @param {string} title — notification title
   * @param {string} body — notification body text
   * @param {object} [options] — extra options
   * @param {string} [options.tag] — dedup tag (same tag replaces previous)
   * @param {function} [options.onClick] — callback when notification is clicked
   */
  const sendNotification = useCallback((title, body, options = {}) => {
    if (!isSupported || permission !== 'granted') return;
    if (!document.hidden) return; // Only fire when tab is not focused

    const notif = new Notification(title, {
      body,
      icon: AEROCHAT_ICON,
      tag: options.tag || 'aerochat-default',
      silent: false,
    });

    notif.onclick = () => {
      window.focus();
      notif.close();
      if (options.onClick) options.onClick();
    };

    // Auto-close after 8 seconds
    setTimeout(() => notif.close(), 8000);
  }, [isSupported, permission]);

  return { isSupported, permission, requestPermission, sendNotification };
}
